import Image from 'next/image'
import { useRouter } from 'next/router';

import { useState, useEffect } from 'react';

import styles from './styles.module.scss';

import { useSession } from 'next-auth/react';
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleMinus, faCircleXmark, faEye, faFlag, faXmark } from '@fortawesome/free-solid-svg-icons';

import errorMsg from '../../../Functions/errorMsg';

import returnDisplayName from '../../../Functions/user/returnDisplayName';
import returnUserProfileImage from '../../../Functions/user/returnUserProfileImage';
import returnProfileBackgroundImage from '../../../Functions/user/returnProfileBackgroundImage';

import { BarLoader, PulseLoader } from 'react-spinners';

import FeedComponent from './FeedComponent/FeedComponent';
import ListingsComponent from './ListingsComponent/ListingsComponent';
import DetailsComponent from './DetailsComponent/DetailsComponent';
import QuickMessage from './QuickMessage/QuickMessage';
import ReportUser from './ReportUser/ReportUser';

export default function OnlineProfile({ profileUsername, closeProfile }) {
    const router = useRouter();

    const { data: session, status} = useSession();

    const [loadingProfileData, setLoadingProfileData] = useState(false);
    const [profileData, setProfileData] = useState(null);

    const [navSelected, setNavSelected] = useState('Feed');

    const [loadingHasConvo, setLoadingHasConvo] = useState(false);
    const [hasConvo, setHasConvo] = useState(null);
    const [isBlocked, setIsBlocked] = useState(null);

    useEffect(() => {
        if(!profileUsername || loadingProfileData) return;
        if(status === 'loading') return;

        const controller = new AbortController();

        async function getUserProfile() {
            setLoadingProfileData(true);
            const result = await axios.post(`${window.location.origin}/api/profile/getUserProfile`, {
                signal: controller.signal,
                username: profileUsername,
                requestingUserGetID: session?.user?.token?.id || null
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
                closeProfile();
            })

            if(result && result.status === 200) {
                const data = result.data;
                setProfileData(data);
                setHasConvo(data.hasConvo);
                setIsBlocked(data.isBlocked)
            }

            setLoadingProfileData(false);
        }

        getUserProfile();

      return () => {
        controller.abort();
      }
    }, [status])

    function loadingData() {
        if(loadingProfileData) return true;
        if(!profileData) return true;
        return false;
    }

    function userViewType() {
        if(status === 'loading') return 'no-user';
        if(!session) return 'no-user';
        if(session.user.token.username === profileUsername) return 'owner';
        if(session.user.token.username !== profileUsername) return 'visitor'
        return 'no-user'
    }

    function messageButtonState() {
        if(loadingHasConvo === true) return 'loading';
        if(hasConvo === null) return 'no-show';
        if(session.user.token.username === profileData) return 'no-show'
        if(hasConvo.hasConvo === true) return 'show-current';
        if(hasConvo.hasConvo === false) return 'show-new'
    }

    function handleOpenCurrentConversation() {
        if(!hasConvo) return errorMsg("Could not find this conversation, please try again later");
        if(hasConvo.hasConvo === false) return errorMsg("You do not have a conversation with this user.");

        const { query } = router;

        const updatedQuery = { ...query, openConversationID: hasConvo.convoID };

        router.push({ pathname: router.pathname, query: updatedQuery });
        closeProfile();
    }

    const [openMessage, setOpenMessage] = useState(false);
    const [openReport, setOpenReport] = useState(false);

    const [pendingBlock, setPendingBlock] = useState(false);

    async function handleBlockUser() {
        if(pendingBlock) return;

        setPendingBlock(true);
        const result = await axios.post(`${window.location.origin}/api/account/blockHelpers/blockHandler`, {
            requestingUserID: session.user.token.id,
            toBlockUser: profileUsername,
            action: isBlocked ? 'unblock' : 'block'
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {
            if (isBlocked) {
                setIsBlocked(false)
            } else {
                setIsBlocked(true);
            }
        }
        
        setPendingBlock(false);
    }

    function blockButtonState() {
        if(pendingBlock) return 'loading';
        if(hasConvo === null) return 'loading';
        if(isBlocked === null) return
        return 'visible'
    }

  return (
    <section id={styles.profileWrapper}>
        <FontAwesomeIcon onClick={closeProfile} icon={faXmark} id={styles.exit} />
        { openMessage && <QuickMessage handleHasNewConvo={(data) => setHasConvo(data)} profileUsername={profileData.username} closeMessageBox={() => setOpenMessage(false)} /> }
        { openReport && <ReportUser profileUsername={profileData.username} closeReportBox={() => setOpenReport(false)} /> }
        { loadingData() === true && <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}><PulseLoader size={30} color='#154734' /></div> }
        { loadingData() === false && <div>
            <section id={styles.backgroundImage}>
                <Image sizes='auto auto' fill objectFit='cover' src={returnProfileBackgroundImage(profileData.profileInfo.backgroundImage, profileUsername).url} alt={returnProfileBackgroundImage(profileData.profileInfo.backgroundImage, profileUsername).alt} />
            </section>
            <section id={styles.basicInfo}>
                <FontAwesomeIcon style={{display: userViewType() === 'visitor' ? 'flex' : 'none'}} onClick={() => setOpenReport(true)} icon={faFlag} id={styles.report} />
                <div> <Image sizes='auto auto' fill objectFit='cover' src={returnUserProfileImage(profileData.profilePicture, profileUsername).url} alt={returnUserProfileImage(profileData.profilePicture, profileUsername).alt} /> </div>
                <h1>{returnDisplayName(profileUsername, profileData.profileInfo.displayName)}</h1>
                <h2>{joinDateString(profileData.profileInfo.joined)}</h2>
                <p style={{display: profileData.profileInfo.bio === '' ? 'none' : 'flex'}}>{profileData.profileInfo.bio}</p>
                <span style={{display: userViewType() === 'visitor' ? 'flex' : 'none'}}>
                    { messageButtonState() === 'loading' && <button> <BarLoader color='white' /> </button> }
                    { messageButtonState() !== 'loading' && messageButtonState() !== 'no-show' && <>
                    { messageButtonState() === 'show-new' && <button onClick={() => setOpenMessage(true)}><FontAwesomeIcon icon={faEye} id={styles.icon} /> Message</button> }
                    { messageButtonState() === 'show-current' && <button onClick={handleOpenCurrentConversation}><FontAwesomeIcon icon={faEye} id={styles.icon} /> View Convo</button> }
                    </> }
                    <button onClick={handleBlockUser}>
                        { blockButtonState() === 'loading' ? <BarLoader color='white' /> : isBlocked ? 'Unblock User' : 'Block User' }
                    </button>
                </span>
            </section>
            <section id={styles.contentSection}>
                <nav id={styles.navigation}>
                    <button className={navSelected === 'Feed' ? styles.selected : {}} onClick={() => setNavSelected('Feed')}>Feed</button>
                    <button className={navSelected === 'Listings' ? styles.selected : {}} onClick={() => setNavSelected('Listings')}>Listings</button>
                    <button className={navSelected === 'Details' ? styles.selected : {}} onClick={() => setNavSelected('Details')}>Details</button>
                </nav>
                <div id={styles.content}>
                    { navSelected === 'Feed' && <FeedComponent /> }
                    { navSelected === 'Listings' && <ListingsComponent profileUsername={profileData.username} /> }
                    { navSelected === 'Details' && <DetailsComponent profileData={profileData} /> }
                </div>
            </section>

        </div> }
    </section>

  )
}













const TrustFactorRating = ({ profileData }) => {

    function returnRating() {
        var standing = null;

        var totalPoints = 0;

        // LOW per is 2 points
        // MED per is 5 points
        // HIGH per is 20 points

        // Excellent is 0 points
        // Good is between 2-7 points;
        // Fair is 8-20 points;
        // Bad is 20 points OR account suspension;

        // automatic account suspension = 21 or more points;

        if(profileData.trustFactorReports !== undefined) {
            profileData.trustFactorReports.forEach((report) => {
                if(report.threat === 'Low') {
                    totalPoints = totalPoints += 2;
                }
                if(report.threat === 'Medium') {
                    totalPoints = totalPoints += 5;
                }
                if(report.threat === 'High') {
                    totalPoints = totalPoints += 20;
                }
            })
        }

        if(totalPoints === 0) {
            standing = 'Excellent'
        } else if(totalPoints > 0 && totalPoints <= 7) {
            standing = 'Good'
        } else if(totalPoints > 7 && totalPoints <= 20) {
            standing = 'Fair'
        } else if(totalPoints > 20) {
            standing = 'Bad'
        }

        return standing;
    }

    function returnIcon() {
        const getStanding = returnRating();

        if(getStanding === 'Excellent') {
            return <FontAwesomeIcon icon={faCircleCheck} color='#154734' className={styles.icon} />
        } else if(getStanding === 'Good') {
            return <FontAwesomeIcon icon={faCircleCheck} color='#43a882' className={styles.icon} />
        } else if(getStanding === 'Fair') {
            return <FontAwesomeIcon style={{borderColor: '#000000', backgroundColor: '#000000'}} icon={faCircleMinus} color='#FFB81C' className={styles.icon} />
        } else if(getStanding === 'Bad') {
            return <FontAwesomeIcon icon={faCircleXmark} color='#CA0B00' className={styles.icon} />
        }
    }

    function returnColor() {
        const getStanding = returnRating();

        if(getStanding === 'Excellent') {
            return '#154734'
        } else if(getStanding === 'Good') {
            return '#43a882'
        } else if(getStanding === 'Fair') {
            return '#FFB81C'
        } else if(getStanding === 'Bad') {
            return '#CA0B00'
        }
    }

    return (
        <span style={{borderColor: returnColor(), backgroundColor: returnColor(), color: returnRating() === 'Fair' ? '#000000' : '#FFFFFF'}}>
            <h1>Trust-Factor Rating</h1>
            <h2>{returnRating()} Standing</h2>
            {returnIcon()}
        </span>
    )
}

function joinDateString(creationDate) {
    if(!creationDate) return;
    const date = new Date(Date.parse(creationDate));
    const getMonth= ["Jan","Feb","March","April","May","June","July","August","Sept","Oct","Nov","Dec"];
    let dateOrdinal;

    const day = date.getDate();
    const month = getMonth[date.getMonth()]
    const year = date.getFullYear();

    if(day === 1 || day === 21 || day === 31) {
        dateOrdinal = 'st'
    } else if(day === 2 || day === 22) {
        dateOrdinal = 'nd'
    } else if(day === 3 || day === 23) {
        dateOrdinal = 'rd'
    } else {
        dateOrdinal = 'th'
    }
    return "Member since " + month + ' ' + day + dateOrdinal + ", " + year;
}